/**
 * The text side of UI blocks (board t5xf): finding spec fences in a reply,
 * compiling their patches and turning them into markdown. It imports nothing,
 * on purpose: the chat bundle, copy, read-aloud and the digest all need it, and
 * json-render + zod belong in `./ui-blocks` (catalog, validation), which the SPA
 * loads only when a block is on screen. The barrel exports this file only.
 */

export const UI_BLOCK_FENCE = 'spec'

export const UI_BLOCK_LIMITS = {
  /** UTF-8 is not measured: characters of the fence body. */
  specChars: 64_000,
  elements: 12,
  rows: 500,
  columns: 24,
  categories: 50,
  series: 8,
} as const

export type UiBlockCell = string | number | null

export const UI_BLOCK_COLUMN_TYPES = ['text', 'number', 'date', 'currency'] as const
export type UiBlockColumnType = (typeof UI_BLOCK_COLUMN_TYPES)[number]

/** json-render's flat spec, structurally: `Spec` without importing it. */
export interface UiBlockSpec {
  root: string
  elements: Record<string, Record<string, unknown>>
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * "1,2", "1.2", "2 450 000", "1 234,5", "1,234,567.8" → number, else null. A
 * single separator is a decimal ("1,234" = 1.234), which is a guess: use this
 * for sorting what is shown as written, never to produce a number that is shown.
 */
export function parseLocaleNumber(raw: string): number | null {
  const text = raw.trim().replace(/\s/g, '').replace(/^[−–]/, '-')
  if (!/^[+-]?\d[\d.,]*$|^[+-]?[.,]\d+$/.test(text)) return null
  const commas = (text.match(/,/g) ?? []).length
  const dots = (text.match(/\./g) ?? []).length
  let normal = text
  if (commas > 0 && dots > 0) {
    const decimal = text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.'
    const group = decimal === ',' ? '.' : ','
    normal = text.split(group).join('').replace(decimal, '.')
  } else if (commas > 1) normal = text.split(',').join('')
  else if (dots > 1) normal = text.split('.').join('')
  else normal = text.replace(',', '.')
  const value = Number(normal)
  return Number.isFinite(value) ? value : null
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

export interface ExtractedUiBlock {
  /** `content.slice(start, end)` is the block, fence lines included. */
  start: number
  end: number
  /** Non-empty body lines, in order (a partial last line excluded). */
  lines: string[]
  /** The compiled patches — never null, possibly empty. */
  spec: UiBlockSpec
  /** Lines that are not a JSON patch HUB accepts. */
  badLines: number
  /** Lines that parsed only after a structural repair. */
  repairedLines: number
  /** Strings with no key, dropped by a repair: the reply must say something is missing. */
  orphanStrings: number
  /** No fence around it (a model slip). */
  bare: boolean
  /** The closing fence arrived (always true for bare blocks). */
  closed: boolean
  /** No closing fence, and prose follows `end`. */
  proseAfter: boolean
  /** The reply ended inside a line: the reply must say something is missing. */
  cutOff: boolean
  /** Unfinished last line of an unclosed fence, held back from `spec`. */
  partialLine: string | null
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

type PatchLine = { op: string; path: string; value?: unknown; from?: string }

/** Only /root and /elements/<key>… may be written: never /state or `__proto__`. */
const PATCH_PATH = /^\/(root$|elements\/[^/]+(\/.*)?$)/

function parsePatch(text: string): PatchLine | null {
  try {
    const value = JSON.parse(text) as Partial<PatchLine> | null
    if (!value || typeof value.op !== 'string' || typeof value.path !== 'string') return null
    if (!PATCH_PATH.test(value.path)) return null
    if (/\/(__proto__|prototype|constructor)(\/|$)/.test(value.path)) return null
    return value as PatchLine
  } catch {
    return null
  }
}

function parsePatchLine(line: string): PatchLine | null {
  const text = line.trim()
  return text.startsWith('{') ? parsePatch(text) : null
}

/**
 * The commonest broken line from both models: one brace too many or too few at
 * the end of a long line. Only ever used on a line known to be complete.
 */
export function balanceBraces(text: string): string | null {
  const stack: string[] = []
  let inString = false
  let escaped = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
    } else if (ch === '"') inString = true
    else if (ch === '{' || ch === '[') stack.push(ch)
    else if (ch === '}' || ch === ']') {
      if (stack.length === 0) return /^[}\]\s]*$/.test(text.slice(i)) ? text.slice(0, i) : null
      if (stack.pop() !== (ch === '}' ? '{' : '[')) return null
    }
  }
  if (inString) return null
  return (
    text +
    stack
      .reverse()
      .map((open) => (open === '{' ? '}' : ']'))
      .join('')
  )
}

/**
 * `"delta":"+0,7 MSEK","+4,9 %"}`: a string with no key. It has no place in the
 * data, so it is dropped, never joined to a neighbour: a repair must not write a
 * value the model did not write. The block says so (`orphanStrings`).
 */
const ORPHAN_STRING = /("(?:[^"\\]|\\.)*"\s*:\s*"(?:[^"\\]|\\.)*")\s*,\s*"(?:[^"\\]|\\.)*"\s*\}/g

/** `{…}},"children":[]}`: the object was complete and "children" came after it. */
const TRAILING_CHILDREN = /\}\s*,\s*"children"\s*:\s*(\[[^\]]*\])\s*\}*\s*$/

/** Tool-call syntax leaking into the text (`</arg_value>`, `</invoke>`): never data. */
const TOOL_SYNTAX = /(<\/[\w:-]+>\s*)+$/
const isToolSyntax = (line: string) => TOOL_SYNTAX.test(line) && line.trim().startsWith('</')

interface RepairedLine {
  patch: PatchLine
  orphanStrings: number
}

/** Structure only: closers, a misplaced "children", a keyless string. No value changes. */
function repairPatchLine(line: string): RepairedLine | null {
  const text = line.replace(TOOL_SYNTAX, '').trimEnd()
  const trailing = TRAILING_CHILDREN.exec(text)
  const shapes = [text]
  if (trailing) shapes.push(text.slice(0, trailing.index + 1))
  for (const orphans of [false, true]) {
    for (const shape of shapes) {
      const candidate = orphans ? shape.replace(ORPHAN_STRING, '$1}') : shape
      if (orphans && candidate === shape) continue
      const balanced = balanceBraces(candidate)
      const patch = balanced ? parsePatch(balanced) : null
      if (!patch) continue
      if (shape !== text) {
        const children = JSON.parse(trailing![1]!) as unknown[]
        if (children.length > 0) (patch as { children?: unknown }).children = children
      }
      return { patch, orphanStrings: orphans ? (shape.match(ORPHAN_STRING)?.length ?? 0) : 0 }
    }
  }
  return null
}

/** RFC 6902 add/replace/remove on a checked path; models use nothing else. */
function applyPatch(spec: UiBlockSpec, patch: PatchLine): boolean {
  if (!['add', 'replace', 'remove'].includes(patch.op)) return false
  const keys = patch.path
    .slice(1)
    .split('/')
    .map((key) => key.replace(/~1/g, '/').replace(/~0/g, '~'))
  const last = keys.pop()!
  let at = spec as unknown as Record<string, unknown>
  for (const key of keys) {
    if (typeof at[key] !== 'object' || at[key] === null) {
      if (patch.op === 'remove') return false
      at[key] = {}
    }
    at = at[key] as Record<string, unknown>
  }
  if (Array.isArray(at)) {
    const index = last === '-' ? at.length : Number(last)
    if (!Number.isInteger(index) || index < 0 || index > at.length) return false
    if (patch.op === 'add') at.splice(index, 0, patch.value)
    else if (patch.op === 'replace') at[index] = patch.value
    else at.splice(index, 1)
    return true
  }
  if (patch.op === 'remove') delete at[last]
  else at[last] = patch.value
  return true
}

interface CompiledLines {
  spec: UiBlockSpec
  badLines: number
  repairedLines: number
  orphanStrings: number
}

function compile(lines: string[]): CompiledLines {
  const spec: UiBlockSpec = { root: '', elements: {} }
  let badLines = 0
  let repairedLines = 0
  let orphanStrings = 0
  for (const line of lines) {
    let patch = parsePatchLine(line)
    if (!patch && line.trim().startsWith('{')) {
      const repaired = repairPatchLine(line.trim())
      if (repaired) {
        patch = repaired.patch
        repairedLines++
        orphanStrings += repaired.orphanStrings
      }
    }
    if (!patch) {
      badLines++
      continue
    }
    // A brace too early puts "children" beside "value": a common slip.
    const { children } = patch as { children?: unknown }
    if (Array.isArray(children) && children.length > 0 && isRecord(patch.value)) {
      patch.value.children ??= children
    }
    if (!applyPatch(spec, patch)) badLines++
  }
  return { spec, badLines, repairedLines, orphanStrings }
}

/**
 * ```spec is also how someone labels an RSpec or OpenAPI snippet: that is code
 * and must stay in the reply. A UI block starts with a patch, or with something
 * that can still become or be repaired into one.
 */
export function looksLikeUiBlock(lines: string | string[]): boolean {
  const list = (typeof lines === 'string' ? lines.split('\n') : lines).filter((l) => l.trim())
  const first = list[0]?.trim()
  if (first === undefined) return true
  try {
    JSON.parse(first)
    return parsePatch(first) !== null
  } catch {
    if (/^\{\s*"op"\s*:/.test(first)) return true
    return list.length === 1 && '{"op"'.startsWith(first)
  }
}

const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})\s*([\w-]*)/
const fenceCloses = (line: string, marker: string) => {
  const trimmed = line.trim()
  return trimmed.length >= marker.length && trimmed === marker[0]!.repeat(trimmed.length)
}

const isPatchShaped = (line: string) => line.trim().startsWith('{') || isToolSyntax(line)

/** The whole line arrived: closing it added nothing that was cut off. */
function isWholeLine(line: string): boolean {
  const text = line.trim().replace(TOOL_SYNTAX, '').trimEnd()
  const balanced = balanceBraces(text)
  return balanced !== null && balanced.length <= text.length && parsePatch(balanced) !== null
}

/** A fence body → its compiled lines. Without a closing fence, prose ends the block. */
function splitFenceBody(
  body: string[],
  closed: boolean,
  nextFence = false,
  final = false
): CompiledLines & { lines: string[]; partialLine: string | null; cutOff: boolean } {
  const proseAt = closed ? -1 : body.findIndex((l) => l.trim() && !isPatchShaped(l))
  const lines = (proseAt >= 0 ? body.slice(0, proseAt) : body).filter(
    (l) => l.trim() && !isToolSyntax(l)
  )
  // Only the very end of an open fence can still be arriving.
  const last = lines.at(-1)
  const open = !closed && !nextFence && proseAt < 0 && last !== undefined && !parsePatchLine(last)
  if (open && final) {
    // The turn is over, so nothing is still arriving. Keep what can be read; a
    // line that had to be closed lost its end, and the reply must say so.
    const closedLine = closePartialJson(last!)
    const readable = [last!, closedLine].find((l) => l && compile([l]).badLines === 0)
    const complete = [...lines.slice(0, -1), ...(readable ? [readable] : [])]
    return {
      lines: complete,
      ...compile(complete),
      partialLine: null,
      cutOff: !isWholeLine(last!),
    }
  }
  const complete = open ? lines.slice(0, -1) : lines
  return {
    lines: complete,
    ...compile(complete),
    partialLine: open ? last! : null,
    cutOff: false,
  }
}

/**
 * Every spec block of `content`: ```spec fences, plus runs of bare patch lines
 * outside any fence. An unclosed fence runs to its last patch line.
 */
export function extractUiBlocks(
  content: string,
  options?: { final?: boolean }
): { blocks: ExtractedUiBlock[]; hasBare: boolean } {
  const blocks: ExtractedUiBlock[] = []
  if (!content.includes('{')) return { blocks, hasBare: false }
  const lines = content.split('\n')
  let offset = 0
  let hasBare = false
  let i = 0
  const lineEnd = (index: number) => offset + (lines[index]?.length ?? 0)

  while (i < lines.length) {
    const line = lines[i]!.replace(/\r$/, '')
    const open = FENCE_OPEN.exec(line)
    if (open) {
      const marker = open[1]!
      const isSpec = open[2] === UI_BLOCK_FENCE
      const start = offset
      const body: string[] = []
      let cursor = lineEnd(i) + 1
      let j = i + 1
      let closed = false
      let nextFence = false
      for (; j < lines.length; j++) {
        const inner = lines[j]!.replace(/\r$/, '')
        if (fenceCloses(inner, marker)) {
          closed = true
          break
        }
        // The model forgot to close it and opened another one.
        if (isSpec && FENCE_OPEN.exec(inner)?.[2]) {
          nextFence = true
          break
        }
        body.push(inner)
        cursor += lines[j]!.length + 1
      }
      const end = closed ? cursor + lines[j]!.length : content.length
      if (isSpec && looksLikeUiBlock(body)) {
        const parts = splitFenceBody(body, closed, nextFence, options?.final)
        const firstProse = closed ? -1 : body.findIndex((l) => l.trim() && !isPatchShaped(l))
        const proseAt = firstProse < 0 && nextFence ? body.length : firstProse
        if (proseAt >= 0) {
          // No closing fence and prose follows: the block ends at its last patch
          // line, the prose stays prose and the scan goes on from it.
          let blockEnd = lineEnd(i)
          let at = blockEnd + 1
          for (let k = 0; k < proseAt; k++) {
            if (body[k]!.trim()) blockEnd = at + lines[i + 1 + k]!.length
            at += lines[i + 1 + k]!.length + 1
          }
          blocks.push({ start, end: blockEnd, ...parts, bare: false, closed, proseAfter: true })
          offset = at
          i = i + 1 + proseAt
          continue
        }
        blocks.push({ start, end, ...parts, bare: false, closed, proseAfter: false })
      }
      offset = closed ? end + 1 : content.length
      i = closed ? j + 1 : lines.length
      continue
    }

    if (parsePatchLine(line)) {
      const start = offset
      const body: string[] = []
      let end = offset
      let j = i
      let cursor = offset
      for (; j < lines.length; j++) {
        const inner = lines[j]!.replace(/\r$/, '')
        if (!parsePatchLine(inner)) break
        body.push(inner)
        end = cursor + lines[j]!.length
        cursor = end + 1
      }
      hasBare = true
      blocks.push({
        start,
        end,
        lines: body,
        ...compile(body),
        bare: true,
        closed: true,
        proseAfter: false,
        partialLine: null,
        cutOff: false,
      })
      offset = cursor
      i = j
      continue
    }

    offset = lineEnd(i) + 1
    i++
  }
  return { blocks, hasBare }
}

const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/

/** Pipe tables in `text`: a separator row that has a pipe and a row above it. */
export function countMarkdownTables(text: string): number {
  const lines = text.split('\n')
  return lines.filter(
    (line, i) => line.includes('|') && TABLE_SEPARATOR.test(line) && lines[i - 1]?.includes('|')
  ).length
}

/** `content` without its blocks: what the model wrote as prose. */
export function uiBlockProse(content: string, blocks: ExtractedUiBlock[]): string {
  let out = ''
  let at = 0
  for (const block of blocks) {
    out += content.slice(at, block.start)
    at = block.end
  }
  return out + content.slice(at)
}

/** One spec spread over several fences: the fragments compiled as one, in order. */
export function mergeUiBlocks(blocks: ExtractedUiBlock[]): ExtractedUiBlock {
  const lines = blocks.flatMap((block) => block.lines)
  return { ...blocks[0]!, lines, ...compile(lines), end: blocks.at(-1)!.end }
}

/** The one size measure, so Worker and SPA can never disagree about the cap. */
export const uiBlockChars = (block: Pick<ExtractedUiBlock, 'lines'>) =>
  block.lines.join('\n').length

export const uiBlockFence = (block: Pick<ExtractedUiBlock, 'lines'>) =>
  `\`\`\`${UI_BLOCK_FENCE}\n${block.lines.join('\n')}\n\`\`\``

/**
 * Text markdown can render: a fence around bare patch lines, and a closing fence
 * where prose followed an open one (or the prose would render as code).
 */
export function normalizeUiBlockFences(content: string): string {
  if (!content.includes('{"op"')) return content
  const { blocks } = extractUiBlocks(content)
  let out = ''
  let at = 0
  for (const block of blocks) {
    if (!block.bare && !block.proseAfter) continue
    out += content.slice(at, block.start) + uiBlockFence(block)
    at = block.end
  }
  return out + content.slice(at)
}

/** The spec of one fence body (the SPA gets the body from react-markdown). */
export function compileUiBlockSource(source: string, closed: boolean): ExtractedUiBlock {
  const body = source.split('\n')
  return {
    start: 0,
    end: source.length,
    ...splitFenceBody(body, closed),
    bare: false,
    closed,
    proseAfter: false,
  }
}

/**
 * Closes a JSON line cut off mid-stream at its last complete value, so a table
 * can grow row by row even though one element is one JSONL line.
 */
export function closePartialJson(line: string): string | null {
  const stack: string[] = []
  let inString = false
  let escaped = false
  let safeEnd = -1
  let safeStack: string[] = []
  const mark = (end: number) => {
    safeEnd = end
    safeStack = [...stack]
  }
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') inString = true
    else if (ch === '{' || ch === '[') stack.push(ch)
    else if (ch === '}' || ch === ']') {
      stack.pop()
      mark(i + 1)
    } else if (ch === ',') mark(i)
  }
  if (safeEnd < 0) return null
  const closers = safeStack.reverse().map((open) => (open === '{' ? '}' : ']'))
  return line.slice(0, safeEnd) + closers.join('')
}

/** The block as far as it can be read now, the unfinished last line closed for display. */
export function partialUiBlockSpec(block: ExtractedUiBlock): UiBlockSpec {
  const repaired = block.partialLine ? closePartialJson(block.partialLine) : null
  return repaired ? compile([...block.lines, repaired]).spec : block.spec
}

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------

const mdCell = (value: unknown): string =>
  value === null || value === undefined
    ? ''
    : String(value)
        .replace(/\s*\n\s*/g, ' ')
        .replace(/\|/g, '\\|')

function markdownTable(header: string[], rows: unknown[][]): string {
  const line = (cells: unknown[]) => `| ${cells.map(mdCell).join(' | ')} |`
  return [
    line(header),
    line(header.map(() => '---')),
    ...rows.map((row) => line(header.map((_, i) => row[i]))),
  ].join('\n')
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

const columnHeader = (column: unknown): string => {
  if (!isRecord(column)) return mdCell(column)
  const label = text(column.label) ?? ''
  const unit = text(column.unit)
  return unit ? `${label} (${unit})` : label
}

/** `DataTable`-shaped props → title, pipe table, note. Tolerates invalid optionals. */
export function dataTableToMarkdown(props: {
  title?: unknown
  columns?: unknown
  rows?: unknown
  note?: unknown
}): string | null {
  if (!Array.isArray(props.columns) || props.columns.length === 0) return null
  if (!Array.isArray(props.rows)) return null
  const rows = props.rows
    .filter((r): r is unknown[] => Array.isArray(r))
    .slice(0, UI_BLOCK_LIMITS.rows)
  const parts: string[] = []
  const title = text(props.title)
  if (title) parts.push(`**${title}**`)
  parts.push(markdownTable(props.columns.map(columnHeader), rows))
  const note = text(props.note)
  if (note) parts.push(`_${note}_`)
  return parts.join('\n\n')
}

function chartToMarkdown(props: Record<string, unknown>): string | null {
  if (!Array.isArray(props.categories) || !Array.isArray(props.series)) return null
  const series = props.series.filter(isRecord)
  if (series.length === 0) return null
  const unit = text(props.unit)
  const names = series.map((s) => {
    const name = text(s.name) ?? ''
    return unit ? `${name} (${unit})` : name
  })
  const rows = props.categories.map((category, i) => [
    category,
    ...series.map((s) => (Array.isArray(s.values) ? s.values[i] : null)),
  ])
  const title = text(props.title)
  const table = markdownTable(['', ...names], rows)
  return title ? `**${title}**\n\n${table}` : table
}

function statToMarkdown(props: Record<string, unknown>): string | null {
  const label = text(props.label)
  const value = text(props.value) ?? (typeof props.value === 'number' ? String(props.value) : null)
  if (!label || !value) return null
  const delta = text(props.delta)
  const hint = text(props.hint)
  return `**${label}:** ${value}${delta ? ` (${delta})` : ''}${hint ? ` — ${hint}` : ''}`
}

/** One element by what its props look like — the type or wrapper may be the invalid part. */
function elementToMarkdown(raw: unknown): string | null {
  if (!isRecord(raw)) return null
  const props = isRecord(raw.props) ? raw.props : raw
  return dataTableToMarkdown(props) ?? chartToMarkdown(props) ?? statToMarkdown(props)
}

/** Tree order from the root, then anything unreachable (a broken spec's survivors). */
function elementsInOrder(spec: UiBlockSpec): unknown[] {
  const elements = isRecord(spec.elements) ? spec.elements : {}
  const seen = new Set<string>()
  const out: unknown[] = []
  const visit = (key: string) => {
    if (seen.has(key)) return
    seen.add(key)
    const element = elements[key]
    if (!element) return
    out.push(element)
    if (Array.isArray(element.children)) element.children.forEach((c) => visit(String(c)))
  }
  visit(spec.root)
  Object.keys(elements).forEach(visit)
  return out
}

/** The readable version of a spec: tables and bold lines, '' when nothing survives. */
export function uiBlockSpecToMarkdown(spec: UiBlockSpec): string {
  return elementsInOrder(spec)
    .flatMap((element) => elementToMarkdown(element) ?? [])
    .join('\n\n')
}

/**
 * `content` with every spec block replaced by markdown. `unrenderable` stands
 * in for a block nothing could be salvaged from (default: nothing).
 */
export function uiBlocksToMarkdown(content: string, options?: { unrenderable?: string }): string {
  if (!content.includes('{')) return content
  const { blocks } = extractUiBlocks(content, { final: true })
  if (blocks.length === 0) return content
  let out = ''
  let at = 0
  for (const block of blocks) {
    out += content.slice(at, block.start)
    out += uiBlockSpecToMarkdown(block.spec) || (options?.unrenderable ?? '')
    at = block.end
  }
  return (out + content.slice(at)).replace(/\n{3,}/g, '\n\n').trimEnd()
}
